import * as Sentry from "@sentry/browser"
import { configureSegment } from "./analytics"
import { loadMainApp } from "./elmApps/mainApp"
import { initPusher } from "./pusher"
import { marketplaceIsParent, setMarketplaceAuthFlag } from "./util/checkMarketplace"
import { fetchConfig } from "./util/fetchConfig"
import { configureSentry } from "./util/sentry"
import { configureVitally } from "./util/tags"
import { neverThrow } from "./util/util"
import "./webcomponents/vendored/elm-rte-toolkit"

// Ensure HTTPS
if (!IS_DEVELOPMENT && location.protocol != "https:") {
  location.href = "https:" + window.location.href.substring(window.location.protocol.length)
}

async function init() {
  const mainContainer = document.getElementById("mainApp")

  if (mainContainer) {
    const config = await fetchConfig()

    // Sentry has to be configured before any calls to Sentry.startSpan or no
    // spans will be sent!
    configureSentry(config)

    neverThrow(() => {
      setMarketplaceAuthFlag()
    })

    /*
    |--------------------------------------------------------------------------|
    | Main App Helpers                                                         |
    |--------------------------------------------------------------------------|
    |
    |
    | If the main app at app.vendr.com is being loaded, these functions
    | provide some setup.
    |
    | 1. Load analytics via segment.
    | 2. Load Sentry and provide context depending on the existing jwt if there
    |    is one.
    */
    void Sentry.startSpan({ name: "loading analytics", op: "app.init.analytics" }, async () => {
      try {
        /**
         * We want to allow Fullstory to record the auth flow from Marketplace while the app is nested,
         * in an Iframe. Since Fullstory is configured through Segment, we want to make sure to set this
         * flag before Fullstory loads.
         *
         *
         * Relevant Fullstory docs: https://help.fullstory.com/hc/en-us/articles/360020622514-Can-Fullstory-capture-content-that-is-presented-in-iframes#h_01F1G333PYKPGZ4B42WDBV3YKV
         */
        if (marketplaceIsParent()) {
          neverThrow(() => {
            window._fs_run_in_iframe = true
          })
        }

        await configureSegment(config)
      } catch (err) {
        Sentry.captureException(err)
      }
      configureVitally(config)
    })

    Sentry.startSpan({ name: "initializing page tags", op: "app.init.tags" }, () => {
      initPusher(config)
    })

    await loadMainApp(config, mainContainer)

    // initialize webcomponents
    //
    // We're loading all of these webcomponents asynchronously after starting
    // the Elm app because the first thing Elm will do is start the
    // authentication routine, after which it'll make requests for data, etc.
    // We want to get that kicked off immediately! If that eventually gets
    // fast enough or precomputed, we should set up a port to tell Elm that all
    // the webcomponents are loaded and that it's safe to draw the main
    // app view.
    await Sentry.startSpan(
      { name: "loading webcomponents", op: "app.init.webcomponents" },
      async () =>
        await Promise.all([
          import("./webcomponents/date-picker"),
          import("./webcomponents/flatfile").then((mod) => mod.configureFlatfile(config)),
          import("./webcomponents/showMore"),
          import("./webcomponents/sizeObserver"),
          import("./webcomponents/Ui/algolia"),
          import("./webcomponents/Ui/drag-and-drop"),
          import("./webcomponents/Ui/draggable"),
          import("./webcomponents/Ui/dropdown"),
          import("./webcomponents/Ui/elm-portal"),
          import("./webcomponents/Ui/sortable-drag-and-drop"),
          import("./webcomponents/vendr-inbox").then((mod) => mod.configureVendrInbox(config)),
          import("./webcomponents/vendr-inbox-toggle"),
          // PLOPCURSOR webcomponentImports
          // (but please keep these sorted)
        ]),
    )
  }
}

// We wait until the window is fully loaded (that is, all blocking scripts have
// loaded) before running. This means we're sure that `window.Elm` and Sentry
// are fully initialized.
window.addEventListener("load", init)
